import {
  DEFAULT_EMBED_SETTINGS,
  type EmbedSettings,
  EVENT_TYPE_COLOURS,
  type EventTypeInput,
  eventTypeInputSchema,
  formatMoney,
  type Locale,
  type LocalizedString,
  minorUnitDigits,
  parseMoneyInput,
  pickLocale,
  type Question,
  type RefundPolicy,
  stripeDashboardUrl,
} from '@bb/shared';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useCurrentUser } from '@/features/auth/use-current-user';
import { isSubscriber, SubscriberOnly } from '@/features/billing/subscriber-only';
import {
  eventTypeQuery,
  useConnections,
  useCreateEventType,
  useSchedules,
  useUpdateEventType,
} from '@/features/booking/api';
import { LocalizedField } from '@/features/booking/localized-field';
import { PHONE_FALLBACK, videoLocationFor } from '@/features/booking/video-location';
import { useMembers } from '@/features/workspace/api';
import { useZoomConnection } from '@/features/zoom/api';
import { ApiRequestError } from '@/lib/api';
import { btnPrimary, btnSecondary, btnSmall, inputCls } from '@/lib/ui';

const TABS = ['details', 'scheduling', 'form', 'payment', 'embed'] as const;
type Tab = (typeof TABS)[number];

/** Which tab a field lives on, keyed by the first segment of its schema path. */
const FIELD_TABS: Record<string, Tab> = {
  name: 'details',
  description: 'details',
  slug: 'details',
  colour: 'details',
  active: 'details',
  location: 'details',
  durationMinutes: 'scheduling',
  slotIntervalMinutes: 'scheduling',
  bufferBeforeMin: 'scheduling',
  bufferAfterMin: 'scheduling',
  minNoticeMinutes: 'scheduling',
  maxDaysAhead: 'scheduling',
  dailyCap: 'scheduling',
  scheduleId: 'scheduling',
  reminders: 'scheduling',
  maxBookerReschedules: 'scheduling',
  rescheduleNoticeMinutes: 'scheduling',
  bookerCanCancel: 'scheduling',
  questions: 'form',
  afterBooking: 'form',
  price: 'payment',
  refundPolicy: 'payment',
  paymentDescription: 'payment',
  visibility: 'details',
  embed: 'embed',
};

/** Schema keys whose label key in the catalog is spelled differently. */
const FIELD_LABEL_KEYS: Record<string, string> = {
  durationMinutes: 'duration',
  slotIntervalMinutes: 'slotInterval',
  bufferBeforeMin: 'bufferBefore',
  bufferAfterMin: 'bufferAfter',
  minNoticeMinutes: 'minNotice',
  scheduleId: 'schedule',
  rescheduleNoticeMinutes: 'rescheduleNotice',
};

/** API error codes that are really complaints about one field. */
const CODE_FIELDS: Record<string, string> = {
  slug_taken: 'slug',
  invalid_slug: 'slug',
  slot_interval_too_long: 'slotIntervalMinutes',
  schedule_not_found: 'scheduleId',
  payments_not_connected: 'price',
  payments_not_ready: 'price',
  currency_mismatch: 'price',
};

/** Field path (dot-joined) -> error code or raw validator message. */
type FieldErrors = Record<string, string>;

const tabFor = (path: string): Tab | undefined => FIELD_TABS[path.split('.')[0] ?? ''];

export const Route = createFileRoute('/app/booking/event-types/$id')({
  validateSearch: (s: Record<string, unknown>): EditorSearch => ({
    ...(typeof s.tab === 'string' && (TABS as readonly string[]).includes(s.tab)
      ? { tab: s.tab as Tab }
      : {}),
    // Set by the Stripe Connect callback when it sends the host back here.
    ...(s.connected === '1' || s.connected === 1 ? { connected: '1' as const } : {}),
    ...(typeof s.error === 'string' && /^[a-z_]{1,40}$/.test(s.error) ? { error: s.error } : {}),
  }),
  loader: async ({ context, params }) => {
    if (params.id === 'new') return { eventType: null };
    return { eventType: await context.queryClient.ensureQueryData(eventTypeQuery(params.id)) };
  },
  component: EventTypeEditorPage,
});

type Draft = EventTypeInput;

interface EditorSearch {
  tab?: Tab;
  connected?: '1';
  error?: string;
}

/**
 * Everything the host has typed but not saved. Stashed before leaving for
 * Stripe Connect so the editor comes back exactly as they left it: same tab,
 * same unsaved values. Session storage, because it belongs to this tab only
 * and must not outlive the browser session.
 */
interface StashedDraft {
  draft: Draft;
  priceText: string;
  slugTouched: boolean;
  at: number;
}

const stashKey = (id: string) => `bb:event-type-draft:${id}`;
/** A stash older than this is stale; the host went to Stripe and never came back. */
const STASH_TTL_MS = 60 * 60 * 1000;

function stashDraft(id: string, value: Omit<StashedDraft, 'at'>): void {
  try {
    sessionStorage.setItem(stashKey(id), JSON.stringify({ ...value, at: Date.now() }));
  } catch {
    // Private mode or a full quota: the draft is lost, which is no worse than before.
  }
}

/** Reads and removes the stash, so a later visit starts clean. */
function takeStashedDraft(id: string): StashedDraft | null {
  try {
    const raw = sessionStorage.getItem(stashKey(id));
    sessionStorage.removeItem(stashKey(id));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StashedDraft;
    if (!parsed?.draft || Date.now() - parsed.at > STASH_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function slugify(input: string): string {
  return (
    input
      .toLowerCase()
      .normalize('NFKD')
      // Accents decompose into combining marks; drop them so Émilie -> emilie.
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60)
  );
}

function EventTypeEditorPage() {
  const { id } = Route.useParams();
  const { eventType } = Route.useLoaderData();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['booking', 'common', 'errors', 'account']);
  const { data: user } = useCurrentUser();
  const { data: schedules = [] } = useSchedules();
  const { data: members = [] } = useMembers();
  // Only people who have actually joined can be named: an invitation that has
  // not been accepted has no account behind it to consult a calendar for.
  const activeMembers = members.filter((m) => m.status === 'active');
  /** Everybody who could take this, the owner included. */
  const { data: connections = [] } = useConnections();
  const { data: zoom } = useZoomConnection();
  const create = useCreateEventType();
  const update = useUpdateEventType();
  const locale = (user?.locale ?? 'en') as Locale;
  const isNew = id === 'new';

  const writeConnection = connections.find(
    (c) => c.writeCalendarExternalId && c.status === 'active',
  );
  const writeProvider = writeConnection?.provider;
  const writeTargetIsPersonal = writeConnection?.personalAccount === true;
  // Three states, not two: no account, an account Stripe has not cleared for
  // charges yet, and one that is ready. The middle one used to look like the
  // first, so a host who had just connected was told to connect again.
  const stripeConnected = Boolean(user?.stripeConnect);
  const canCharge = Boolean(user?.stripeConnect?.chargesEnabled);
  const currency = user?.stripeConnect?.defaultCurrency?.toUpperCase() ?? 'JPY';
  /**
   * The same two actions as on the Payments page, but "check the status" sends
   * them there rather than re-asking Stripe from here: the status, the badges
   * and the dashboard button all live together on that page, so that is where
   * someone chasing an approval wants to be.
   */
  const pendingLinks = {
    dash: (
      // biome-ignore lint/a11y/useAnchorContent: Trans fills the content in
      <a
        href={stripeDashboardUrl(user?.stripeConnect?.livemode ?? false)}
        target="_blank"
        rel="noreferrer"
        className="underline"
      />
    ),
    status: <Link to="/app/booking/payments" className="underline" />,
  };

  // Restored once, at mount, so a redraw never clobbers what the host is typing.
  const [stashed] = useState(() => takeStashedDraft(id));

  const [draft, setDraft] = useState<Draft>(
    () =>
      stashed?.draft ??
      (eventType
        ? {
            ...eventType,
            description: eventType.description ?? {},
            paymentDescription: eventType.paymentDescription ?? {},
            embed: eventType.embed ?? DEFAULT_EMBED_SETTINGS,
          }
        : {
            name: {},
            description: {},
            slug: '',
            colour: '#171717',
            durationMinutes: 30,
            bufferBeforeMin: 0,
            bufferAfterMin: 0,
            minNoticeMinutes: 240,
            maxDaysAhead: 60,
            dailyCap: 0,
            location: videoLocationFor(connections) ?? PHONE_FALLBACK,
            questions: [],
            afterBooking: { kind: 'confirmation_page' },
            reminders: [1440],
            embed: DEFAULT_EMBED_SETTINGS,
            maxBookerReschedules: 2,
            rescheduleNoticeMinutes: null,
            bookerCanCancel: true,
            // Empty means the owner takes it, which is what a new event type is.
            hosts: [],
            scheduleId: '',
            active: true,
            payOnArrival: false,
            refundPolicy: { kind: 'none' },
            paymentDescription: {},
            visibility: 'public',
          }),
  );

  /*
    What the location being chosen needs from the person taking it. Three of the
    kinds depend on the individual, and only Zoom is obvious — Meet and Teams
    are minted by the calendar write, so they need the right provider as that
    person's write target.
  */
  const needs = (() => {
    const kind = draft.location?.kind;
    if (kind === 'zoom') return { key: 'zoom' as const, label: 'Zoom' };
    if (kind === 'google_meet') return { key: 'googleMeet' as const, label: 'Google Meet' };
    if (kind === 'teams') return { key: 'teams' as const, label: 'Microsoft Teams' };
    return null;
  })();

  const takenBy = [
    ...(user ? [{ id: user.id, label: t('booking:eventTypes.fields.takenByMe'), warn: null }] : []),
    ...activeMembers.map((m) => ({
      id: m.agentId as string,
      label: m.name ?? m.email,
      // Said here as well as on the Members screen, because this is the moment
      // somebody is put into a rotation and the consequence lands. The provider
      // gap comes first: it stops this event type working for them entirely,
      // where a missing calendar only costs the diary entry.
      warn:
        needs && !m.provides[needs.key]
          ? t('booking:eventTypes.fields.takenByCannot', {
              provider: needs.label,
              name: m.name ?? m.email,
            })
          : m.calendar === 'ok'
            ? null
            : t(`account:members.calendar.${m.calendar}`),
    })),
  ];
  const [slugTouched, setSlugTouched] = useState(stashed?.slugTouched ?? !isNew);

  /**
   * How this event type is paid for, as a choice rather than a side effect.
   *
   * It was inferred from the numbers: a price meant paid, an empty price meant
   * free, and a flag beside it meant in person. Nobody could be expected to
   * work that out, and emptying a field is a poor way to say "this is free
   * now". Held in its own state because selecting "paid" has to reveal the
   * price field before there is a price to infer it from.
   */
  const [payMode, setPayMode] = useState<'free' | 'online' | 'in_person'>(() => {
    const priced = Boolean(eventType?.price && eventType.price.amount > 0);
    if (!priced) return 'free';
    return eventType?.payOnArrival ? 'in_person' : 'online';
  });
  const [priceText, setPriceText] = useState(
    () =>
      stashed?.priceText ??
      (eventType?.price && eventType.price.amount > 0
        ? (eventType.price.amount / 10 ** minorUnitDigits(eventType.price.currency)).toString()
        : ''),
  );
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const scrollToError = useRef(false);

  // Default schedule once loaded.
  useEffect(() => {
    if (!draft.scheduleId && schedules.length > 0) {
      const def = schedules.find((s) => s.isDefault) ?? schedules[0];
      if (def) setDraft((d) => ({ ...d, scheduleId: def.id }));
    }
  }, [schedules, draft.scheduleId]);

  const patch = (p: Partial<Draft>) => setDraft((d) => ({ ...d, ...p }));

  const onNameChange = (name: LocalizedString) => {
    const next: Partial<Draft> = { name };
    if (!slugTouched) next.slug = slugify(pickLocale(name, 'en', locale));
    patch(next);
  };

  const priceAmount = useMemo(
    () => (priceText ? parseMoneyInput(priceText, currency) : 0),
    [priceText, currency],
  );

  const search = Route.useSearch();
  const requested = search.tab;
  const tab: Tab = requested && (requested !== 'embed' || !isNew) ? requested : 'details';

  // The Connect round trip reports its outcome in the URL. Keep it in state and
  // tidy the URL, so a reload does not show the same flash again.
  const [connectResult] = useState<{ ok: boolean; code?: string } | null>(() =>
    search.connected ? { ok: true } : search.error ? { ok: false, code: search.error } : null,
  );
  useEffect(() => {
    if (!search.connected && !search.error) return;
    void navigate({
      to: '/app/booking/event-types/$id',
      params: { id },
      search: { ...(requested ? { tab: requested } : {}) },
      replace: true,
    });
  }, [search.connected, search.error, requested, navigate, id]);

  /**
   * Leaving for Stripe reloads the whole app, so the draft is stashed first and
   * picked up again when Stripe sends the host back to this tab.
   */
  const onConnectStripe = () => stashDraft(id, { draft, priceText, slugTouched });
  const connectHref = `/api/v1/payments/connect?returnTo=${encodeURIComponent(
    `/app/booking/event-types/${id}?tab=payment`,
  )}`;
  const errorTabs = new Set(
    Object.keys(fieldErrors)
      .map(tabFor)
      .filter((x): x is Tab => !!x),
  );
  const goToTab = (next: Tab) =>
    void navigate({
      to: '/app/booking/event-types/$id',
      params: { id },
      search: { tab: next },
      replace: true,
    });

  /** Codes from the schema and API become sentences; anything else gets a generic nudge. */
  const fieldMessage = (raw: string): string =>
    /^[a-z_]+$/.test(raw) && i18n.exists(`errors:${raw}`)
      ? t(`errors:${raw}`)
      : t('errors:invalid_value');

  /** First error at any of the given paths, or nested beneath them. */
  const fieldError = (...paths: string[]): string | undefined => {
    for (const path of paths) {
      const hit = Object.keys(fieldErrors).find((k) => k === path || k.startsWith(`${path}.`));
      if (hit) return fieldMessage(fieldErrors[hit] ?? '');
    }
    return undefined;
  };

  const labelFor = (path: string): string => {
    const [head = '', ...rest] = path.split('.');
    switch (head) {
      case 'location':
        return t('booking:eventTypes.sections.location');
      case 'afterBooking':
        return t('booking:eventTypes.sections.afterBooking');
      case 'questions':
        return t('booking:eventTypes.questions.nth', { n: Number(rest[0] ?? 0) + 1 });
      case 'embed':
        return t('booking:eventTypes.tabs.embed');
      default: {
        const key = `booking:eventTypes.fields.${FIELD_LABEL_KEYS[head] ?? head}`;
        return i18n.exists(key) ? t(key) : head;
      }
    }
  };

  /** Show the errors, and jump to the first tab that has one. */
  const fail = (errors: FieldErrors, code: string) => {
    setFieldErrors(errors);
    setError(code);
    const first = TABS.find((k) => Object.keys(errors).some((path) => tabFor(path) === k));
    scrollToError.current = Object.keys(errors).length > 0;
    if (first && first !== tab) goToTab(first);
  };

  // Once the right tab is showing, bring the first invalid field into view.
  useEffect(() => {
    if (!scrollToError.current) return;
    const el = document.querySelector<HTMLElement>('[data-field-error]');
    if (!el) return;
    scrollToError.current = false;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.querySelector<HTMLElement>('input, select, textarea')?.focus({ preventScroll: true });
  });

  const save = () => {
    setError(null);
    setFieldErrors({});
    // A paid mode with no amount is a mistake worth naming, not something to
    // quietly save as free.
    if (payMode === 'online' && !canCharge) {
      fail(
        { price: stripeConnected ? 'payments_not_ready' : 'payments_not_connected' },
        'validation',
      );
      return;
    }
    if (payMode !== 'free' && !(priceAmount && priceAmount > 0)) {
      fail({ price: 'price_required' }, 'validation');
      return;
    }
    const candidate: Draft = {
      ...draft,
      payOnArrival: payMode === 'in_person',
      // Null, not undefined: an absent key means "leave the price alone" on an
      // update, which is how a cleared price used to survive.
      price: payMode === 'free' ? null : { amount: priceAmount as number, currency },
    };
    const parsed = eventTypeInputSchema.safeParse(candidate);
    if (!parsed.success) {
      const errors: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const path = issue.path.join('.');
        if (path && !errors[path]) errors[path] = issue.message;
      }
      fail(errors, 'validation');
      return;
    }
    const onError = (err: unknown) => {
      if (!(err instanceof ApiRequestError)) return fail({}, 'generic');
      if (err.code === 'validation' && Array.isArray(err.details)) {
        const errors: FieldErrors = {};
        for (const d of err.details as { path?: string; message?: string }[]) {
          const path = (d.path ?? '').replace(/^\//, '').replaceAll('/', '.');
          if (path && !errors[path]) errors[path] = d.message ?? 'invalid_value';
        }
        return fail(errors, 'validation');
      }
      // A code about one field is shown on that field, under the generic heading.
      const field = CODE_FIELDS[err.code];
      fail(field ? { [field]: err.code } : {}, field ? 'validation' : err.code);
    };
    if (isNew) {
      create.mutate(parsed.data, {
        onSuccess: () => void navigate({ to: '/app/booking' }),
        onError,
      });
    } else {
      update.mutate(
        { id, ...parsed.data, price: parsed.data.price ?? null },
        { onSuccess: () => void navigate({ to: '/app/booking' }), onError },
      );
    }
  };

  const pending = create.isPending || update.isPending;

  return (
    <div className="max-w-2xl space-y-8">
      <h2 className="text-base font-medium">
        {isNew ? t('booking:eventTypes.new') : t('booking:eventTypes.edit')}
      </h2>

      <TabBar id={id} active={tab} isNew={isNew} errorTabs={errorTabs} />

      {tab === 'details' ? (
        <>
          <Section title={t('booking:eventTypes.sections.basics')}>
            <LocalizedField
              label={t('booking:eventTypes.fields.name')}
              value={draft.name}
              onChange={onNameChange}
              required
              error={fieldError('name')}
            />
            <LocalizedField
              label={t('booking:eventTypes.fields.description')}
              value={draft.description ?? {}}
              onChange={(description) => patch({ description })}
              multiline
              error={fieldError('description')}
            />
            <div className="grid gap-4 sm:grid-cols-[1fr_120px]">
              <Field
                label={t('booking:eventTypes.fields.slug')}
                help={t('booking:eventTypes.fields.slugHelp')}
                error={fieldError('slug')}
              >
                <div className="flex flex-col gap-1 text-sm sm:flex-row sm:items-center">
                  <span className="truncate text-neutral-400">
                    {window.location.origin}/{user?.username ?? '…'}/
                  </span>
                  <input
                    value={draft.slug}
                    onChange={(e) => {
                      setSlugTouched(true);
                      patch({ slug: slugify(e.target.value) });
                    }}
                    className={inputCls}
                    spellCheck={false}
                  />
                </div>
              </Field>
              <Field label={t('booking:eventTypes.fields.colour')}>
                {/*
                  Swatches first, picker beside them. The colour is what tells
                  two event types apart on the calendar, and choosing one that
                  already reads well there should not mean fighting an eyedropper.
                */}
                <div className="flex flex-wrap items-center gap-1.5">
                  {EVENT_TYPE_COLOURS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      aria-label={c}
                      aria-pressed={draft.colour.toLowerCase() === c}
                      onClick={() => patch({ colour: c })}
                      style={{ backgroundColor: c }}
                      className={`size-7 rounded-full ${
                        draft.colour.toLowerCase() === c
                          ? 'ring-2 ring-neutral-900 ring-offset-2'
                          : ''
                      }`}
                    />
                  ))}
                  <input
                    type="color"
                    value={draft.colour}
                    onChange={(e) => patch({ colour: e.target.value })}
                    className="h-9 w-12 rounded border border-neutral-300"
                  />
                </div>
              </Field>
            </div>
            <label className="flex min-h-11 items-center gap-2 text-sm sm:min-h-0">
              <input
                type="checkbox"
                checked={draft.active}
                onChange={(e) => patch({ active: e.target.checked })}
              />
              {t('booking:eventTypes.fields.active')}
            </label>
            <Field
              label={t('booking:eventTypes.fields.visibility')}
              help={t(`booking:eventTypes.visibility.${draft.visibility}Help`)}
            >
              <select
                value={draft.visibility}
                onChange={(e) => patch({ visibility: e.target.value as Draft['visibility'] })}
                className={inputCls}
              >
                {(['public', 'invite_only'] as const).map((v) => (
                  <option key={v} value={v}>
                    {t(`booking:eventTypes.visibility.${v}`)}
                  </option>
                ))}
              </select>
            </Field>
          </Section>
          <Section title={t('booking:eventTypes.sections.location')}>
            <div className="flex flex-wrap gap-2">
              {(['google_meet', 'teams', 'zoom', 'phone', 'in_person', 'custom'] as const).map(
                (kind) => (
                  <button
                    key={kind}
                    type="button"
                    onClick={() =>
                      patch({
                        location:
                          kind === 'phone'
                            ? { kind, direction: 'host_calls' }
                            : kind === 'in_person'
                              ? { kind, address: '' }
                              : kind === 'custom'
                                ? { kind, text: {} }
                                : { kind },
                      })
                    }
                    className={`min-h-11 rounded border px-3 py-1.5 text-sm sm:min-h-0 ${
                      draft.location.kind === kind
                        ? 'border-neutral-900 bg-neutral-900 text-white'
                        : 'border-neutral-300 bg-white'
                    }`}
                  >
                    {t(`booking:eventTypes.location.${kind}`)}
                  </button>
                ),
              )}
            </div>
            {draft.location.kind === 'google_meet' && writeProvider !== 'google' ? (
              <p className="text-xs text-amber-700">
                {t('booking:eventTypes.location.needsGoogle')}
              </p>
            ) : null}
            {draft.location.kind === 'teams' && writeProvider !== 'microsoft' ? (
              <p className="text-xs text-amber-700">
                {t('booking:eventTypes.location.needsMicrosoft')}
              </p>
            ) : null}
            {draft.location.kind === 'teams' && writeTargetIsPersonal ? (
              // Microsoft refuses to create Teams meetings for consumer
              // accounts, so this would produce bookings that name Teams and
              // give the customer nothing to click.
              <p className="text-xs text-amber-700">
                {t('booking:eventTypes.location.teamsNeedsWorkAccount')}
              </p>
            ) : null}
            {draft.location.kind === 'zoom' && !zoom?.connection ? (
              <p className="text-xs text-amber-700">
                {t('booking:eventTypes.location.needsZoom')}{' '}
                <Link
                  to="/app/settings/integrations"
                  search={{ tab: 'zoom' }}
                  className="underline"
                >
                  {t('booking:eventTypes.location.needsZoomLink')}
                </Link>
              </p>
            ) : null}
            {draft.location.kind === 'zoom' && zoom?.connection?.status === 'needs_reauth' ? (
              <p className="text-xs text-amber-700">
                {t('booking:eventTypes.location.zoomNeedsReauth')}
              </p>
            ) : null}
            {/*
              The free plan cuts a call off at 40 minutes. Said here rather than
              on the Zoom tab because it is only a problem in combination with
              this service's length, which is the number on this screen.
            */}
            {draft.location.kind === 'zoom' &&
            zoom?.connection?.planType === 'free' &&
            draft.durationMinutes > 40 ? (
              <p className="text-xs text-amber-700">
                {t('booking:eventTypes.location.zoomFreeLimit')}
              </p>
            ) : null}
            {draft.location.kind === 'phone' ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t('booking:eventTypes.location.phoneDirection')}>
                  <select
                    value={draft.location.direction}
                    onChange={(e) =>
                      patch({
                        location: {
                          ...draft.location,
                          kind: 'phone',
                          direction: e.target.value as 'host_calls' | 'booker_calls',
                        },
                      })
                    }
                    className={inputCls}
                  >
                    <option value="host_calls">
                      {t('booking:eventTypes.location.host_calls')}
                    </option>
                    <option value="booker_calls">
                      {t('booking:eventTypes.location.booker_calls')}
                    </option>
                  </select>
                </Field>
                {draft.location.direction === 'booker_calls' ? (
                  <Field
                    label={t('booking:eventTypes.location.hostPhone')}
                    error={fieldError('location.hostPhone')}
                  >
                    <input
                      value={draft.location.hostPhone ?? ''}
                      onChange={(e) =>
                        patch({
                          location: {
                            kind: 'phone',
                            direction: 'booker_calls',
                            hostPhone: e.target.value,
                          },
                        })
                      }
                      className={inputCls}
                    />
                  </Field>
                ) : null}
              </div>
            ) : null}
            {draft.location.kind === 'in_person' ? (
              <Field
                label={t('booking:eventTypes.location.address')}
                error={fieldError('location.address')}
              >
                <input
                  value={draft.location.address}
                  onChange={(e) =>
                    patch({ location: { kind: 'in_person', address: e.target.value } })
                  }
                  className={inputCls}
                />
              </Field>
            ) : null}
            {draft.location.kind === 'custom' ? (
              <LocalizedField
                label={t('booking:eventTypes.location.customText')}
                value={draft.location.text}
                onChange={(text) => patch({ location: { kind: 'custom', text } })}
                multiline
                error={fieldError('location.text')}
              />
            ) : null}
          </Section>
        </>
      ) : null}

      {tab === 'scheduling' ? (
        <Section title={t('booking:eventTypes.sections.scheduling')}>
          <div className="grid gap-4 sm:grid-cols-2">
            <NumberField
              label={t('booking:eventTypes.fields.duration')}
              value={draft.durationMinutes}
              onChange={(v) => patch({ durationMinutes: v })}
              error={fieldError('durationMinutes')}
              unit={t('booking:eventTypes.units.minutes', { count: draft.durationMinutes })}
              min={5}
              max={720}
              step={5}
            />
            <NumberField
              label={t('booking:eventTypes.fields.slotInterval')}
              value={draft.slotIntervalMinutes ?? draft.durationMinutes}
              onChange={(v) => patch({ slotIntervalMinutes: v })}
              error={fieldError('slotIntervalMinutes')}
              unit={t('booking:eventTypes.units.minutes', {
                count: draft.slotIntervalMinutes ?? draft.durationMinutes,
              })}
              min={5}
              max={draft.durationMinutes}
              step={5}
            />
            <NumberField
              label={t('booking:eventTypes.fields.bufferBefore')}
              value={draft.bufferBeforeMin}
              onChange={(v) => patch({ bufferBeforeMin: v })}
              error={fieldError('bufferBeforeMin')}
              unit={t('booking:eventTypes.units.minutes', { count: draft.bufferBeforeMin })}
              min={0}
              max={120}
              step={5}
            />
            <NumberField
              label={t('booking:eventTypes.fields.bufferAfter')}
              value={draft.bufferAfterMin}
              onChange={(v) => patch({ bufferAfterMin: v })}
              error={fieldError('bufferAfterMin')}
              unit={t('booking:eventTypes.units.minutes', { count: draft.bufferAfterMin })}
              min={0}
              max={120}
              step={5}
            />
            <NumberField
              label={t('booking:eventTypes.fields.minNotice')}
              value={draft.minNoticeMinutes / 60}
              onChange={(v) => patch({ minNoticeMinutes: v * 60 })}
              error={fieldError('minNoticeMinutes')}
              unit={t('booking:eventTypes.units.hours', { count: draft.minNoticeMinutes / 60 })}
              min={0}
              max={720}
              step={1}
            />
            <NumberField
              label={t('booking:eventTypes.fields.maxDaysAhead')}
              value={draft.maxDaysAhead}
              onChange={(v) => patch({ maxDaysAhead: v })}
              error={fieldError('maxDaysAhead')}
              unit={t('booking:eventTypes.units.days', { count: draft.maxDaysAhead })}
              min={1}
              max={365}
              step={1}
            />
            <NumberField
              label={t('booking:eventTypes.fields.dailyCap')}
              value={draft.dailyCap}
              onChange={(v) => patch({ dailyCap: v })}
              error={fieldError('dailyCap')}
              unit={draft.dailyCap === 0 ? t('booking:eventTypes.fields.dailyCapUnlimited') : ''}
              min={0}
              max={100}
              step={1}
            />
            <Field label={t('booking:eventTypes.fields.schedule')} error={fieldError('scheduleId')}>
              <select
                value={draft.scheduleId}
                onChange={(e) => patch({ scheduleId: e.target.value })}
                className={inputCls}
              >
                {schedules.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.timezone})
                  </option>
                ))}
              </select>
            </Field>
            {/*
              Only shown once there is somebody to pick, so a solo account never
              sees a control with one option in it.
            */}
            {activeMembers.length > 0 ? (
              <Field label={t('booking:eventTypes.fields.takenBy')} error={fieldError('hosts')}>
                {/*
                  Checkboxes rather than a multi-select: with nobody ticked the
                  owner takes it, which is the state most pages are in and the
                  one a <select multiple> cannot show without a row that means
                  "none of the below".
                */}
                <div className="space-y-2">
                  {/*
                    The owner is first in the list and ticked like anybody else.
                    Somebody running a salon takes appointments alongside their
                    staff rather than only managing them, and leaving them out
                    made "me and Yuki" impossible to express — the only choices
                    were me alone or Yuki alone.
                  */}
                  {takenBy.map((person) => {
                    const picked = (draft.hosts ?? []).some((h) => h.agentId === person.id);
                    // items-start, not center: a warning makes the label two
                    // lines and the box would float to the middle of them.
                    return (
                      <label key={person.id} className="flex items-start gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={picked}
                          onChange={() =>
                            patch({
                              hosts: picked
                                ? (draft.hosts ?? []).filter((h) => h.agentId !== person.id)
                                : [
                                    ...(draft.hosts ?? []),
                                    { agentId: person.id, role: 'rotating' as const },
                                  ],
                            })
                          }
                          className="mt-0.5 size-4 shrink-0"
                        />
                        <span>
                          {person.label}
                          {person.warn ? (
                            <span className="block text-xs text-amber-700">{person.warn}</span>
                          ) : null}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <p className="mt-1 text-xs text-neutral-500">
                  {(draft.hosts ?? []).length === 0
                    ? t('booking:eventTypes.fields.takenByNobody')
                    : (draft.hosts ?? []).length === 1
                      ? t('booking:eventTypes.fields.takenByOne')
                      : t('booking:eventTypes.fields.takenByMany')}
                </p>
              </Field>
            ) : null}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <NumberField
              label={t('booking:eventTypes.fields.maxBookerReschedules')}
              value={draft.maxBookerReschedules}
              onChange={(v) => patch({ maxBookerReschedules: v })}
              error={fieldError('maxBookerReschedules')}
              unit={
                draft.maxBookerReschedules === 0
                  ? t('booking:eventTypes.fields.rescheduleNever')
                  : t('booking:eventTypes.units.times', { count: draft.maxBookerReschedules })
              }
              min={0}
              max={20}
              step={1}
            />
            <NumberField
              label={t('booking:eventTypes.fields.rescheduleNotice')}
              value={(draft.rescheduleNoticeMinutes ?? draft.minNoticeMinutes) / 60}
              onChange={(v) => patch({ rescheduleNoticeMinutes: v * 60 })}
              error={fieldError('rescheduleNoticeMinutes')}
              unit={t('booking:eventTypes.units.hoursBeforeStart', {
                count: (draft.rescheduleNoticeMinutes ?? draft.minNoticeMinutes) / 60,
              })}
              min={0}
              max={720}
              step={1}
            />
          </div>
          {/*
            Beside the reschedule limit because it is the same kind of control:
            what the booker may do to a booking once it exists. Both apply to
            bookings already taken, which the help text says outright.
          */}
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.bookerCanCancel}
              onChange={(e) => patch({ bookerCanCancel: e.target.checked })}
              className="mt-0.5 size-4"
            />
            <span>
              {t('booking:eventTypes.fields.bookerCanCancel')}
              <span className="mt-0.5 block text-xs text-neutral-500">
                {t('booking:eventTypes.fields.bookerCanCancelHelp')}
              </span>
            </span>
          </label>
          <Field label={t('booking:eventTypes.fields.reminders')} error={fieldError('reminders')}>
            <div className="flex flex-wrap gap-3 text-sm">
              {[
                { minutes: 10080, label: t('booking:eventTypes.units.days', { count: 7 }) },
                { minutes: 1440, label: t('booking:eventTypes.units.days', { count: 1 }) },
                { minutes: 120, label: t('booking:eventTypes.units.hours', { count: 2 }) },
                { minutes: 30, label: t('booking:eventTypes.units.minutes', { count: 30 }) },
              ].map((opt) => (
                <label key={opt.minutes} className="flex min-h-11 items-center gap-1.5 sm:min-h-0">
                  <input
                    type="checkbox"
                    checked={draft.reminders.includes(opt.minutes)}
                    onChange={(e) =>
                      patch({
                        reminders: e.target.checked
                          ? [...draft.reminders, opt.minutes].sort((a, b) => b - a)
                          : draft.reminders.filter((m) => m !== opt.minutes),
                      })
                    }
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </Field>
        </Section>
      ) : null}

      {tab === 'form' ? (
        <>
          <Section title={t('booking:eventTypes.sections.questions')}>
            <QuestionsEditor
              questions={draft.questions}
              onChange={(questions) => patch({ questions })}
              errorFor={fieldError}
            />
          </Section>
          <Section title={t('booking:eventTypes.sections.afterBooking')}>
            <div className="flex gap-2">
              {(['confirmation_page', 'redirect'] as const).map((kind) => (
                <button
                  key={kind}
                  type="button"
                  onClick={() =>
                    patch({
                      afterBooking: kind === 'redirect' ? { kind, redirectUrl: '' } : { kind },
                    })
                  }
                  className={`min-h-11 rounded border px-3 py-1.5 text-sm sm:min-h-0 ${
                    draft.afterBooking.kind === kind
                      ? 'border-neutral-900 bg-neutral-900 text-white'
                      : 'border-neutral-300 bg-white'
                  }`}
                >
                  {t(`booking:eventTypes.afterBooking.${kind}`)}
                </button>
              ))}
            </div>
            {draft.afterBooking.kind === 'confirmation_page' ? (
              <LocalizedField
                label={t('booking:eventTypes.afterBooking.message')}
                value={draft.afterBooking.message ?? {}}
                onChange={(message) =>
                  patch({ afterBooking: { kind: 'confirmation_page', message } })
                }
                multiline
                error={fieldError('afterBooking.message')}
              />
            ) : (
              <Field
                label={t('booking:eventTypes.afterBooking.redirectUrl')}
                error={fieldError('afterBooking.redirectUrl')}
              >
                <input
                  value={draft.afterBooking.redirectUrl}
                  onChange={(e) =>
                    patch({ afterBooking: { kind: 'redirect', redirectUrl: e.target.value } })
                  }
                  className={inputCls}
                  placeholder="https://"
                />
              </Field>
            )}
          </Section>
        </>
      ) : null}

      {tab === 'payment' ? (
        <Section title={t('booking:eventTypes.sections.payment')}>
          {connectResult ? (
            <p
              className={`rounded border px-3 py-2 text-sm ${
                !connectResult.ok
                  ? 'border-red-200 bg-red-50 text-red-700'
                  : canCharge
                    ? 'border-green-200 bg-green-50 text-green-800'
                    : 'border-amber-200 bg-amber-50 text-amber-900'
              }`}
            >
              {!connectResult.ok ? (
                t(`errors:${connectResult.code ?? 'generic'}`)
              ) : canCharge ? (
                t('booking:payments.connectedFlash')
              ) : (
                <Trans i18nKey="booking:payments.connectedPendingFlash" components={pendingLinks} />
              )}
            </p>
          ) : null}
          {/*
            Stated as a choice. It used to be inferred from whether a price had
            been typed, which meant "make this free again" was an empty field
            nobody would think to try.
          */}
          <fieldset>
            <legend className="mb-2 text-sm font-medium">
              {t('booking:eventTypes.payMode.legend')}
            </legend>
            <div className="space-y-1">
              {(['free', 'online', 'in_person'] as const).map((mode) => (
                <label
                  key={mode}
                  className="flex min-h-11 items-start gap-2 text-sm sm:min-h-0 sm:py-1"
                >
                  <input
                    type="radio"
                    name="payMode"
                    checked={payMode === mode}
                    onChange={() => setPayMode(mode)}
                    className="mt-0.5 h-4 w-4"
                  />
                  <span>
                    {t(`booking:eventTypes.payMode.${mode}`)}
                    <span className="mt-0.5 block text-xs text-neutral-500">
                      {t(`booking:eventTypes.payMode.${mode}Help`)}
                    </span>
                    {/* Said at the point of choosing, so nobody picks it,
                        types a price and only then learns it cannot be
                        saved. */}
                    {mode === 'online' && !canCharge ? (
                      <span className="mt-0.5 block text-xs font-medium text-amber-700">
                        {t(
                          stripeConnected
                            ? 'booking:eventTypes.payMode.onlinePending'
                            : 'booking:eventTypes.payMode.onlineNotConnected',
                        )}
                      </span>
                    ) : null}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          {payMode === 'free' ? null : payMode === 'in_person' ? (
            <div className="border-t border-neutral-100 pt-4">
              <Field
                label={`${t('booking:eventTypes.fields.price')} (${currency})`}
                error={fieldError('price')}
              >
                <input
                  value={priceText}
                  onChange={(e) => setPriceText(e.target.value)}
                  inputMode="decimal"
                  placeholder="0"
                  className={inputCls}
                />
                <span className="mt-1 block text-xs text-neutral-500">
                  {priceAmount && priceAmount > 0
                    ? t('booking:eventTypes.payOnArrival.due', {
                        amount: formatMoney({ amount: priceAmount, currency }, locale),
                      })
                    : t('booking:eventTypes.payOnArrival.noPrice')}
                </span>
              </Field>
            </div>
          ) : !isSubscriber(user) && !canCharge ? (
            <div>
              <p className="text-sm text-neutral-600">{t('booking:eventTypes.paymentsLocked')}</p>
              <div className="mt-3">
                <SubscriberOnly feature={t('booking:payments.featureName')} />
              </div>
            </div>
          ) : stripeConnected && !canCharge ? (
            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <p>
                <Trans i18nKey="booking:payments.chargesDisabled" components={pendingLinks} />
              </p>
            </div>
          ) : !canCharge ? (
            <div>
              <p className="text-sm text-neutral-600">{t('booking:eventTypes.paymentsLocked')}</p>
              <p className="mt-1 text-sm text-neutral-500">
                {t('booking:eventTypes.paymentsLockedBody')}
              </p>
              <a
                href={connectHref}
                onClick={onConnectStripe}
                className={`${btnPrimary} mt-3 inline-flex`}
              >
                {t('booking:payments.connect')}
              </a>
            </div>
          ) : (
            <>
              {/* Connected, but the subscription lapsed: the price stays, the bookings do not. */}
              {!isSubscriber(user) ? (
                <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  {t('booking:eventTypes.paidBookingsStopped')}
                </p>
              ) : null}
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label={`${t('booking:eventTypes.fields.price')} (${currency})`}
                  error={fieldError('price')}
                >
                  <input
                    value={priceText}
                    onChange={(e) => setPriceText(e.target.value)}
                    inputMode="decimal"
                    placeholder="0"
                    className={inputCls}
                  />
                  {priceAmount && priceAmount > 0 ? (
                    <span className="mt-1 block text-xs text-neutral-500">
                      {formatMoney({ amount: priceAmount, currency }, locale)}
                    </span>
                  ) : null}
                </Field>
                <Field
                  label={t('booking:eventTypes.fields.refundPolicy')}
                  error={fieldError('refundPolicy.kind')}
                >
                  <select
                    value={draft.refundPolicy.kind}
                    onChange={(e) => {
                      const kind = e.target.value as RefundPolicy['kind'];
                      patch({
                        refundPolicy:
                          kind === 'none'
                            ? { kind }
                            : kind === 'full_before_hours'
                              ? { kind, fullRefundHours: 24 }
                              : {
                                  kind,
                                  fullRefundHours: 24,
                                  partialHours: 2,
                                  partialPercent: 50,
                                },
                      });
                    }}
                    className={inputCls}
                  >
                    {(['none', 'full_before_hours', 'tiered'] as const).map((k) => (
                      <option key={k} value={k}>
                        {t(`booking:eventTypes.refund.${k}`)}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              {draft.refundPolicy.kind !== 'none' ? (
                <div className="grid gap-4 sm:grid-cols-3">
                  <NumberField
                    label={t('booking:eventTypes.refund.fullRefundHours')}
                    value={draft.refundPolicy.fullRefundHours}
                    onChange={(v) =>
                      patch({
                        refundPolicy: {
                          ...draft.refundPolicy,
                          fullRefundHours: v,
                        } as RefundPolicy,
                      })
                    }
                    unit={t('booking:eventTypes.refund.hoursBefore')}
                    min={0}
                    max={720}
                    step={1}
                    error={fieldError('refundPolicy.fullRefundHours')}
                  />
                  {draft.refundPolicy.kind === 'tiered' ? (
                    <>
                      <NumberField
                        label={t('booking:eventTypes.refund.partialHours')}
                        value={draft.refundPolicy.partialHours}
                        onChange={(v) =>
                          patch({
                            refundPolicy: {
                              ...draft.refundPolicy,
                              kind: 'tiered',
                              partialHours: v,
                            } as RefundPolicy,
                          })
                        }
                        unit={t('booking:eventTypes.refund.hoursBefore')}
                        min={0}
                        max={720}
                        step={1}
                        error={fieldError('refundPolicy.partialHours', 'refundPolicy')}
                      />
                      <NumberField
                        label={t('booking:eventTypes.refund.partialPercent')}
                        value={draft.refundPolicy.partialPercent}
                        onChange={(v) =>
                          patch({
                            refundPolicy: {
                              ...draft.refundPolicy,
                              kind: 'tiered',
                              partialPercent: v,
                            } as RefundPolicy,
                          })
                        }
                        unit="%"
                        min={1}
                        max={99}
                        step={5}
                        error={fieldError('refundPolicy.partialPercent')}
                      />
                    </>
                  ) : null}
                </div>
              ) : null}
              <LocalizedField
                label={t('booking:eventTypes.fields.paymentDescription')}
                value={draft.paymentDescription ?? {}}
                onChange={(paymentDescription) => patch({ paymentDescription })}
                error={fieldError('paymentDescription')}
              />
            </>
          )}
        </Section>
      ) : null}

      {tab === 'embed' && !isNew && user?.username ? (
        <EmbedSection
          username={user.username}
          slug={draft.slug}
          embed={draft.embed}
          onChange={(embed) => patch({ embed })}
        />
      ) : null}

      {error ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <p>{t(`errors:${error}`)}</p>
          {Object.keys(fieldErrors).length > 0 ? (
            <ul className="mt-1 space-y-0.5 text-xs">
              {Object.entries(fieldErrors).map(([path, raw]) => {
                const target = tabFor(path);
                return (
                  <li key={path}>
                    <button
                      type="button"
                      onClick={() => {
                        scrollToError.current = true;
                        if (target && target !== tab) goToTab(target);
                      }}
                      className="min-h-11 text-left underline-offset-2 hover:underline sm:min-h-0"
                    >
                      <span className="font-medium">{labelFor(path)}</span>: {fieldMessage(raw)}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      ) : null}

      <div className="flex gap-3">
        <button type="button" onClick={save} disabled={pending} className={btnPrimary}>
          {t('common:actions.save')}
        </button>
        <button
          type="button"
          onClick={() => void navigate({ to: '/app/booking' })}
          className={btnSecondary}
        >
          {t('common:actions.cancel')}
        </button>
      </div>
    </div>
  );
}

function QuestionsEditor({
  questions,
  onChange,
  errorFor,
}: {
  questions: Question[];
  onChange: (q: Question[]) => void;
  errorFor: (...paths: string[]) => string | undefined;
}) {
  const { t } = useTranslation('booking');
  const setAt = (i: number, patch: Partial<Question>) =>
    onChange(questions.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  return (
    <div className="space-y-3">
      {questions.map((q, i) => (
        <div key={q.id} className="space-y-3 rounded-lg border border-neutral-200 bg-white p-3">
          <LocalizedField
            label={t('eventTypes.questions.label')}
            value={q.label}
            onChange={(label) => setAt(i, { label })}
            required
            error={errorFor(`questions.${i}.label`)}
          />
          <div className="flex flex-wrap items-end gap-3">
            <Field label={t('eventTypes.questions.type')}>
              <select
                value={q.type}
                onChange={(e) => setAt(i, { type: e.target.value as Question['type'] })}
                className={inputCls}
              >
                {(['text', 'textarea', 'select', 'checkbox', 'phone'] as const).map((k) => (
                  <option key={k} value={k}>
                    {t(`eventTypes.questions.types.${k}`)}
                  </option>
                ))}
              </select>
            </Field>
            <label className="flex min-h-11 items-center gap-1.5 text-sm sm:min-h-0 sm:pb-2">
              <input
                type="checkbox"
                className="size-4"
                checked={q.required}
                onChange={(e) => setAt(i, { required: e.target.checked })}
              />
              {t('eventTypes.questions.required')}
            </label>
            <button
              type="button"
              onClick={() => onChange(questions.filter((_, j) => j !== i))}
              className="ml-auto flex min-h-11 items-center px-2 text-xs text-red-700 sm:min-h-0 sm:pb-2"
            >
              ×
            </button>
          </div>
          {q.type === 'select' ? (
            <Field
              label={t('eventTypes.questions.options')}
              error={errorFor(`questions.${i}.options`, `questions.${i}`)}
            >
              <textarea
                rows={3}
                value={q.options.map((o) => pickLocale(o.label, 'en')).join('\n')}
                onChange={(e) =>
                  setAt(i, {
                    options: e.target.value
                      .split('\n')
                      .map((line) => line.trim())
                      .filter(Boolean)
                      .map((line) => ({
                        value: slugify(line) || line,
                        label: { en: line, ja: line },
                      })),
                  })
                }
                className={inputCls}
              />
            </Field>
          ) : null}
        </div>
      ))}
      <button
        type="button"
        onClick={() =>
          onChange([
            ...questions,
            {
              id: `q${Date.now().toString(36)}`,
              label: {},
              type: 'text',
              required: false,
              options: [],
            },
          ])
        }
        className={btnSecondary}
      >
        {t('eventTypes.questions.add')}
      </button>
    </div>
  );
}

/** Tabs are links, so the active one survives reloads and can be linked to. */
function TabBar({
  id,
  active,
  isNew,
  errorTabs,
}: {
  id: string;
  active: Tab;
  isNew: boolean;
  errorTabs: Set<Tab>;
}) {
  const { t } = useTranslation('booking');
  return (
    <nav className="-mx-4 flex gap-4 overflow-x-auto border-b border-neutral-200 px-4 text-sm [scrollbar-width:none] sm:mx-0 sm:px-0 [&::-webkit-scrollbar]:hidden">
      {TABS.filter((k) => k !== 'embed' || !isNew).map((k) => (
        <Link
          key={k}
          to="/app/booking/event-types/$id"
          params={{ id }}
          search={{ tab: k }}
          replace
          className={`-mb-px flex min-h-11 shrink-0 items-center border-b-2 ${
            active === k
              ? 'border-neutral-900 font-medium text-neutral-900'
              : 'border-transparent text-neutral-500 hover:text-neutral-800'
          }`}
        >
          {t(`eventTypes.tabs.${k}`)}
          {errorTabs.has(k) ? (
            <span
              className="ml-1.5 inline-block size-2 rounded-full bg-red-600"
              title={t('eventTypes.tabHasErrors')}
            />
          ) : null}
        </Link>
      ))}
    </nav>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4">
      <h3 className="border-b border-neutral-200 pb-1 text-sm font-medium text-neutral-700">
        {title}
      </h3>
      {children}
    </section>
  );
}

/** What the widget uses when a colour is null; must match :root in styles.css. */
const EMBED_FALLBACK = { bg: '#ffffff', fg: '#171717', accent: '#171717' } as const;

/**
 * Embed settings for this event type. They are saved with the event type and
 * the widget reads them live, so the snippet pasted into the host's site never
 * needs to change. The preview passes the unsaved draft as query overrides.
 */
function EmbedSection({
  username,
  slug,
  embed,
  onChange,
}: {
  username: string;
  slug: string;
  embed: EmbedSettings;
  onChange: (embed: EmbedSettings) => void;
}) {
  const { t } = useTranslation(['booking']);
  const [copied, setCopied] = useState<string | null>(null);
  const origin = window.location.origin;
  const publicUrl = `${origin}/${username}/${slug}`;
  const loader = `<script src="${origin}/embed.js" async></script>`;
  const snippets = [
    { key: 'inline', code: `<div data-bb-embed="${publicUrl}"></div>\n${loader}` },
    {
      key: 'popup',
      code: `<button data-bb-popup="${publicUrl}">${t('booking:eventTypes.embed.buttonText')}</button>\n${loader}`,
    },
    {
      key: 'iframe',
      code: `<iframe src="${origin}/embed/${username}/${slug}" title="Booking" style="width:100%;height:720px;border:0"></iframe>`,
    },
  ] as const;

  const copy = async (key: string, code: string) => {
    await navigator.clipboard.writeText(code);
    setCopied(key);
    setTimeout(() => setCopied((c) => (c === key ? null : c)), 1500);
  };

  // Preview: the draft as query overrides, debounced so dragging a colour
  // picker does not reload the frame on every tick.
  const previewParams = new URLSearchParams();
  previewParams.set('details', embed.showDetails ? '1' : '0');
  for (const key of ['bg', 'fg', 'accent'] as const) {
    const v = embed[key];
    if (v) previewParams.set(key, v.slice(1));
  }
  const previewTarget = `${origin}/embed/${username}/${slug}?${previewParams}`;
  const [previewSrc, setPreviewSrc] = useState(previewTarget);
  useEffect(() => {
    const id = setTimeout(() => setPreviewSrc(previewTarget), 300);
    return () => clearTimeout(id);
  }, [previewTarget]);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [frameHeight, setFrameHeight] = useState(520);
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const d = e.data as { type?: string; height?: number } | null;
      if (d?.type !== 'bb:resize' || typeof d.height !== 'number') return;
      if (e.source === frameRef.current?.contentWindow) setFrameHeight(Math.max(160, d.height + 2));
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return (
    <Section title={t('booking:eventTypes.embed.title')}>
      <p className="text-sm text-neutral-600">{t('booking:eventTypes.embed.help')}</p>
      <label className="flex min-h-11 items-center gap-2 text-sm sm:min-h-0">
        <input
          type="checkbox"
          className="size-4"
          checked={!embed.showDetails}
          onChange={(e) => onChange({ ...embed, showDetails: !e.target.checked })}
        />
        {t('booking:eventTypes.embed.hideDetails')}
      </label>
      <div>
        <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
          <span className="text-sm font-medium">{t('booking:eventTypes.embed.colours')}</span>
          <button
            type="button"
            onClick={() => onChange({ ...embed, bg: null, fg: null, accent: null })}
            className="text-xs underline"
          >
            {t('booking:eventTypes.embed.reset')}
          </button>
        </div>
        <p className="mb-2 text-xs text-neutral-500">{t('booking:eventTypes.embed.coloursHelp')}</p>
        <div className="flex flex-wrap gap-4">
          {(['bg', 'fg', 'accent'] as const).map((key) => (
            <label key={key} className="flex min-h-11 items-center gap-2 text-sm">
              <input
                type="color"
                value={embed[key] ?? EMBED_FALLBACK[key]}
                onChange={(e) => onChange({ ...embed, [key]: e.target.value })}
                className="size-9 cursor-pointer rounded border border-neutral-300 bg-white p-0.5"
              />
              {t(`booking:eventTypes.embed.colour_${key}`)}
            </label>
          ))}
        </div>
      </div>
      <div>
        <div className="mb-1 text-sm font-medium">{t('booking:eventTypes.embed.preview')}</div>
        <p className="mb-2 text-xs text-neutral-500">{t('booking:eventTypes.embed.previewHelp')}</p>
        <iframe
          ref={frameRef}
          src={previewSrc}
          title={t('booking:eventTypes.embed.preview')}
          style={{ height: frameHeight }}
          className="w-full rounded-lg border border-dashed border-neutral-300 bg-neutral-50"
        />
      </div>
      {snippets.map((snip) => (
        <div key={snip.key}>
          <div className="mb-1 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <span className="text-sm font-medium">
                {t(`booking:eventTypes.embed.${snip.key}`)}
              </span>
              <span className="ml-2 text-xs text-neutral-500">
                {t(`booking:eventTypes.embed.${snip.key}Help`)}
              </span>
            </div>
            <button
              type="button"
              onClick={() => void copy(snip.key, snip.code)}
              className={btnSmall}
            >
              {copied === snip.key
                ? t('booking:eventTypes.embed.copied')
                : t('booking:eventTypes.embed.copy')}
            </button>
          </div>
          <pre className="overflow-x-auto rounded border border-neutral-200 bg-neutral-50 p-3 text-xs leading-relaxed">
            <code>{snip.code}</code>
          </pre>
        </div>
      ))}
    </Section>
  );
}

/**
 * Label + control. With `error` set, every control inside turns red and the
 * message shows beneath; `data-field-error` lets the page scroll to the first one.
 */
function Field({
  label,
  help,
  error,
  children,
}: {
  label: string;
  help?: string;
  error?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <div
      data-field-error={error ? '' : undefined}
      className={
        error
          ? '[&_input]:border-red-400 [&_select]:border-red-400 [&_textarea]:border-red-400'
          : undefined
      }
    >
      <span className="mb-1 block text-sm font-medium">{label}</span>
      {children}
      {error ? <p className="mt-1 text-xs text-red-700">{error}</p> : null}
      {help && !error ? <p className="mt-1 text-xs text-neutral-500">{help}</p> : null}
    </div>
  );
}

function NumberField(props: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  unit: string;
  min: number;
  max: number;
  step: number;
  error?: string | undefined;
}) {
  /*
    What is in the box while it is being typed in, which is not always a number.

    Bound straight to the number, clearing the field gave `Number('')` — zero —
    so the box refilled itself with a 0 you could not delete, and typing 7 in
    front of it read 07. The draft holds whatever has been typed, including
    nothing; the number only moves when there is one to move.
  */
  const [draft, setDraft] = useState(String(props.value));
  const [typing, setTyping] = useState(false);
  // Changed from somewhere else — switching event type, a form reset — and not
  // by the person in the middle of typing into it.
  if (!typing && draft !== String(props.value)) setDraft(String(props.value));

  return (
    <Field label={props.label} error={props.error}>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={draft}
          min={props.min}
          max={props.max}
          step={props.step}
          onFocus={() => setTyping(true)}
          onChange={(e) => {
            setDraft(e.target.value);
            const n = Number(e.target.value);
            if (e.target.value !== '' && Number.isFinite(n)) props.onChange(n);
          }}
          onBlur={() => {
            setTyping(false);
            // An empty box is not a value. Put back the one that is in force,
            // rather than saving a zero nobody asked for.
            setDraft(String(props.value));
          }}
          className={`${inputCls} sm:w-24`}
        />
        <span className="text-sm text-neutral-500">{props.unit}</span>
      </div>
    </Field>
  );
}
